import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Save, Edit2, Upload, FileSpreadsheet, Loader2, Download, Tag, Sparkles, Calendar as CalendarIcon, AlertTriangle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  listCampaigns, upsertCampaign, deleteCampaign, uploadCampaignsExcel,
  listCampaignLeads, deleteCampaignLead, downloadCampaignLeadsXlsx,
} from "@/lib/api";

const empty = { id: null, title: "", description: "", price_eur: 0, region: "", valid_from: "", valid_until: "", terms: "", active: true };

export default function CampaignsTab() {
  const [campaigns, setCampaigns] = useState([]);
  const [leads, setLeads] = useState([]);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [replace, setReplace] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [c, l] = await Promise.all([listCampaigns(), listCampaignLeads()]);
      setCampaigns(c);
      setLeads(l);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kampanyalar yüklenemedi.");
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title) return toast.error("Kampanya adı gerekli");
    try {
      await upsertCampaign(form);
      toast.success(form.id ? "Güncellendi" : "Eklendi");
      setForm(empty);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kampanya kaydedilemedi.");
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadCampaignsExcel(file, replace);
      toast.success(`${r.inserted} kampanya eklendi (${r.skipped} atlandı)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadLeads = () => {
    window.location.href = downloadCampaignLeadsXlsx();
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="list">
        <TabsList className="bg-secondary/60 p-1 rounded-2xl">
          <TabsTrigger value="list" data-testid="camp-tab-list" className="rounded-xl data-[state=active]:bg-white font-display"><Tag className="w-4 h-4 mr-2" /> Kampanyalar</TabsTrigger>
          <TabsTrigger value="leads" data-testid="camp-tab-leads" className="rounded-xl data-[state=active]:bg-white font-display"><Sparkles className="w-4 h-4 mr-2" /> Kampanya İsteyenler ({leads.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="list" className="mt-6 space-y-6">
          {/* Excel upload */}
          <Card data-testid="camp-excel-card" className="border-primary/30">
            <CardHeader>
              <CardTitle className="font-display flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> Excel ile Toplu Kampanya Yükleme</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">
                Sütunlar: <code className="bg-secondary px-1.5 py-0.5 rounded">title</code> (kampanya/baslik),
                <code className="bg-secondary px-1.5 py-0.5 rounded ml-1">price_eur</code> (fiyat/eur),
                <code className="bg-secondary px-1.5 py-0.5 rounded ml-1">description</code> (açıklama),
                <code className="bg-secondary px-1.5 py-0.5 rounded ml-1">region</code> (bolge),
                <code className="bg-secondary px-1.5 py-0.5 rounded ml-1">valid_from</code> (başlangıç tarihi),
                <code className="bg-secondary px-1.5 py-0.5 rounded ml-1">valid_until</code> (bitiş),
                <code className="bg-secondary px-1.5 py-0.5 rounded ml-1">terms</code> (şartlar).
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-secondary/40 rounded-xl">
                <div>
                  <div className="text-sm font-medium">Mevcut kampanyaları sil ve değiştir</div>
                  <div className="text-xs text-muted-foreground">Açıkken: tüm eski kampanyalar silinir</div>
                </div>
                <Switch data-testid="camp-replace" checked={replace} onCheckedChange={setReplace} />
              </div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onUpload} data-testid="camp-excel-input" className="hidden" />
              <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="camp-excel-btn" className="w-full h-12 rounded-full" style={{ background: "#8B9B88" }}>
                {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                {uploading ? "Yükleniyor..." : "Excel Dosyası Seç ve Yükle"}
              </Button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/template.xlsx`}
                download
                data-testid="campaigns-template-btn"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline mt-1"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" />
                Hazır şablonu indir (örnek satırlarla)
              </a>
              {replace && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900">
                  <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  Yükleme yaptığınızda eski kampanyalar silinecek.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Manual add */}
          <Card>
            <CardHeader>
              <CardTitle className="font-display">{form.id ? "Düzenle" : "Tek Kampanya Ekle"}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-xs">Kampanya Adı</Label>
                  <Input data-testid="camp-title" placeholder="Ör: 3 Seans PicoSil Kış Kampanyası" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Fiyat (EUR €)</Label>
                  <Input data-testid="camp-eur" type="number" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: parseFloat(e.target.value || 0) })} />
                </div>
                <div>
                  <Label className="text-xs">Bölge / Uygulama</Label>
                  <Input data-testid="camp-region" placeholder="Yüz, Karın..." value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Başlangıç</Label>
                  <Input data-testid="camp-from" type="date" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Bitiş</Label>
                  <Input data-testid="camp-until" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
                </div>
              </div>
              <div>
                <Label className="text-xs">Açıklama</Label>
                <Textarea data-testid="camp-desc" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Şartlar</Label>
                <Textarea data-testid="camp-terms" rows={2} placeholder="Ör: Yeni hastalar için, peşin ödeme..." value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} />
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
                <span className="text-sm">Aktif</span>
              </div>
              <div className="flex gap-2">
                <Button data-testid="camp-save" onClick={save} style={{ background: "#B39B7A" }}>
                  <Save className="w-4 h-4 mr-2" /> Kaydet
                </Button>
                {form.id && <Button variant="outline" onClick={() => setForm(empty)}><X className="w-4 h-4 mr-2" /> İptal</Button>}
              </div>
            </CardContent>
          </Card>

          {/* List */}
          <Card>
            <CardHeader><CardTitle className="font-display">Aktif Kampanyalar ({campaigns.length})</CardTitle></CardHeader>
            <CardContent>
              {campaigns.length === 0 ? (
                <div className="text-center text-muted-foreground text-sm py-8">Henüz kampanya yok</div>
              ) : (
                <div className="space-y-2">
                  {campaigns.map((c) => (
                    <div key={c.id} data-testid={`camp-${c.id}`} className="p-4 border border-border/40 rounded-2xl">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="font-medium">{c.title}</div>
                            {c.active ? <Badge style={{ background: "#8B9B88" }}>aktif</Badge> : <Badge variant="secondary">pasif</Badge>}
                            {c.region && <Badge variant="outline" className="text-xs">{c.region}</Badge>}
                          </div>
                          <div className="text-2xl font-serif mt-1" style={{ color: "#B39B7A" }}>€{Number(c.price_eur).toLocaleString("en-US")}</div>
                          {c.description && <div className="text-sm text-muted-foreground mt-2">{c.description}</div>}
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground flex-wrap">
                            {c.valid_from && <span><CalendarIcon className="inline w-3 h-3 mr-1" />Başlangıç: {c.valid_from}</span>}
                            {c.valid_until && <span>· Bitiş: {c.valid_until}</span>}
                          </div>
                          {c.terms && <div className="text-xs text-muted-foreground italic mt-1">Şartlar: {c.terms}</div>}
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" title="Düzenle" onClick={() => { setForm({ ...empty, ...c }); if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid={`camp-edit-${c.id}`}><Edit2 className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="icon" title="Sil" onClick={async () => {
                            try { await deleteCampaign(c.id); toast.success("Silindi"); load(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
                          }} className="text-destructive" data-testid={`camp-del-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="leads" className="mt-6 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="font-display">Kampanya İsteyen Müşteriler ({leads.length})</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Müşteri kampanya sorduğunda önce iletişim formu doldurması zorunludur. Form tamamlananlar buraya kaydedilir.
                    Liste her gün otomatik güncellenir.
                  </p>
                </div>
                <Button data-testid="camp-leads-download" onClick={downloadLeads} disabled={leads.length === 0} style={{ background: "#2C3531" }}>
                  <Download className="w-4 h-4 mr-2" /> Excel İndir
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {leads.length === 0 ? (
                <div className="text-center text-muted-foreground text-sm py-8">Henüz kampanya isteyen müşteri yok</div>
              ) : (
                <div className="space-y-2">
                  {leads.map((l) => (
                    <div key={l.id} data-testid={`camp-lead-${l.id}`} className="p-4 border border-border/40 rounded-xl flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium">{l.name}</div>
                          <Badge variant="secondary" className="text-xs">{l.contact_method}: {l.contact_value}</Badge>
                          <Badge variant="outline" className="text-[10px]">{(l.language || "tr").toUpperCase()}</Badge>
                          <Badge variant="outline" className="text-[10px]">{l.country}</Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">{new Date(l.ts).toLocaleString()}</div>
                      </div>
                      <Button variant="ghost" size="icon" onClick={async () => {
                        try { await deleteCampaignLead(l.id); toast.success("Silindi"); load(); }
                        catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
                      }} className="text-destructive">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
